import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button, QRCode, Steps } from "antd";
import useCountdown from "@/hooks/useCountdown";
import { ChainPayProps } from "@/index";
import failImg from "@/assets/fail.png";
import successImg from "@/assets/success.png";
import clsx from "clsx";

type PaymentProps = {
  code: string;
  link: string;
  channel: string;
  iconName: string;
  isQrCode: boolean;
  countDown: number;
  orderNo: string;
  payState: string;
} & ChainPayProps;

const POLLING_INTERVAL = 1000;

export default function Payment({
  channel,
  amount,
  link,
  code,
  iconName,
  isQrCode,
  currency,
  getChannelApiUrl,
  iconAssetsPrefix,
  orderNo,
  countDown = 15 * 60 * 1000,
  payState,
  onBack,
  onSuccess,
  onError,
  onComputed,
  onCountDownTimeout,
}: PaymentProps) {
  const { t } = useTranslation();

  const steps = [
    {
      title: t("打开{{name}} APP", {
        name: `${channel.charAt(0).toUpperCase()}${channel.slice(1)}`,
      }),
    },
    {
      title: channel === "huione" ? t("扫描下方二维码") : t("扫描新窗口二维码"),
    },
    {
      title: t("确认付款信息并完成支付"),
    },
  ];

  const items = useMemo(
    () =>
      steps.map((item: { title: string }) => ({
        title: item.title,
      })),
    [steps]
  );

  useEffect(() => {
    if (link) {
      window.open(link, "_blank");
    }
  }, [link]);

  const isActive = useRef<boolean>(true);
  const { secondsRemaining, startCountdown, stopCountdown } = useCountdown(
    countDown,
    () => {
      isActive.current = false;
      clearTimeout(timer.current);
      if (!isSuccess) {
        onComputed?.();
        onCountDownTimeout?.();
      }
    }
  );

  const timer = useRef<any>();

  const [payStatus, setPayStatus] = useState<string>("");
  const [isError, setIsError] = useState<boolean>(false);

  const isTimeout = useMemo(
    () => payStatus !== "DONE_PAYMENT" && !secondsRemaining && !isError,
    [payStatus, secondsRemaining, isError]
  );

  const isXPayTimeout = useMemo(
    () => !isQrCode && isTimeout,
    [isQrCode, isTimeout]
  );

  const isSuccess = useMemo(() => payStatus === "DONE_PAYMENT", [payStatus]);

  const setState = (state: string) => {
    setPayStatus(state);

    switch (state) {
      case "NON_PAYMENT":
        timer.current = setTimeout(poll, POLLING_INTERVAL);
        break;
      case "DONE_PAYMENT":
        stopCountdown();
        onSuccess?.();
        timer.current = null;
        onComputed?.();
        break;
      default:
        stopCountdown();
        onError?.("fail");
        onComputed?.();
        setIsError(true);
        timer.current = null;
        break;
    }
  };

  useEffect(() => {
    if (payState) setState(payState);
  }, [payState]);

  const poll = useCallback(async () => {
    if (isTimeout || payState) return;

    try {
      const { state } = await fetch(
        `${getChannelApiUrl}/getOrderStatus?order_no=${orderNo}`
      )
        .then((res) => res.json())
        .then((res) => res.data);

      if (!isActive.current) {
        clearTimeout(timer.current);
        return;
      }

      setState(state);
    } catch (error: any) {
      // console.error('Polling error:', error);
      // setIsError(true)
      if (!isTimeout) timer.current = setTimeout(poll, POLLING_INTERVAL);
      onError?.(error);
    }
  }, [isActive, isTimeout]);

  useEffect(() => {
    poll();

    return () => {
      isActive.current = false;
      clearTimeout(timer.current);
    };
  }, [poll]);

  // const openHuione = () => {
  //   window.open(`huione://huione/openReceive?code=${code}`, "_blank");
  // };

  const BackButton = () => {
    return (
      <div className="w-full flex justify-center items-center gap-[20px]">
        <Button
          color="default"
          variant="filled"
          className="max-w-[50%] flex-1 !mt-[25px] h-[50px] rounded-[40px]"
          onClick={() => onBack?.(isTimeout || isSuccess)}
        >
          {isTimeout || isSuccess ? t("关闭") : t("返回")}
        </Button>
      </div>
    );
  };

  return (
    <>
      {isSuccess || isError || isXPayTimeout ? (
        <div className="flex flex-col items-center mt-[25px] pb-[20px]">
          <div className="flex flex-col items-center gap-[30px]">
            <img
              src={isError || isXPayTimeout ? failImg : successImg}
              className="w-[60px] h-[60px]"
            />
            <span
              className={`font-[700] ${
                isError || isXPayTimeout ? "text-[#f00]" : "text-[#00D52E]"
              } text-[16px]`}
            >
              {isXPayTimeout
                ? t("支付超时")
                : isError
                ? t("支付失败")
                : t("支付成功")}
            </span>
            <span className="font-bold text-[30px]">
              {amount} {currency}
            </span>
          </div>
          <BackButton />
        </div>
      ) : channel === "huione" ? (
        <div className="flex flex-col items-center mt-[25px] pb-[20px]">
          <Steps
            size="small"
            current={-1}
            labelPlacement="vertical"
            className="[&_.ant-steps-item-title]:!text-[12px] md:[&_.ant-steps-item-title]:!leading-[16px]"
            items={items}
          />
          <div className="py-[30px] sm:pt-0 sm:pb-[20px] text-[12px] text-center">
            {secondsRemaining ? (
              <>
                {t("请在付款期限内确认完成支付")}{" "}
                <span
                  className={clsx("text-[#00D52E]", {
                    "text-[#f00]": secondsRemaining < 60,
                  })}
                >
                  {startCountdown()}
                </span>
              </>
            ) : null}
          </div>
          {isQrCode && (
            <div className="relative">
              {isTimeout && (
                <span className="absolute z-10 px-[8px] box-border w-full h-full bg-black/30 dark:bg-white/30 flex justify-center items-center text-center text-white dark:text-black">
                  {t("二维码过期")}
                </span>
              )}
              <QRCode
                errorLevel="H"
                value={`huione://openReceive?code=${code}`}
                icon={`${iconAssetsPrefix}${iconName}`}
                style={{ filter: isTimeout ? "blur(1px)" : "" }}
              />
            </div>
          )}
          <div className="flex items-center gap-[10px] py-[20px]">
            {/* <span className="text-[12px] opacity-70">{t("总计支付")}</span> */}
            <div className="flex items-end text-[36px] leading-none">
              {amount}{" "}
              <span className="text-[20px] pb-[5px] px-[5px]">{currency}</span>
            </div>
          </div>
          <BackButton />
        </div>
      ) : null}
    </>
  );
}
